import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { useMemo } from "react";
import { Transaction } from "@/lib/types";
import { useCurrency } from "@/hooks/useCurrency";

interface IncomeCardProps {
  transactions: Transaction[];
}

/** Sum all income transactions in the specified year + month (0-indexed). */
function monthlyIncome(
  transactions: Transaction[],
  year: number,
  month: number
): number {
  return transactions
    .filter((t) => {
      const d = new Date(t.date);
      return (
        t.type === 'income' &&
        d.getFullYear() === year &&
        d.getMonth() === month
      );
    })
    .reduce((sum, t) => sum + t.amount, 0);
}

export default function IncomeCard({ transactions }: IncomeCardProps) {
  const { fmt } = useCurrency();
  const { currentMonthIncome, previousMonthIncome } = useMemo(() => {
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();
    // Previous month wraps across year boundary
    const prevMonth = curMonth === 0 ? 11 : curMonth - 1;
    const prevYear = curMonth === 0 ? curYear - 1 : curYear;

    return {
      currentMonthIncome: monthlyIncome(transactions, curYear, curMonth),
      previousMonthIncome: monthlyIncome(transactions, prevYear, prevMonth),
    };
  }, [transactions]);

  const changeAmount = currentMonthIncome - previousMonthIncome;
  // [T38P0] Use real prev-month figure; fall back to 0% if no previous data
  const changePercentage =
    previousMonthIncome > 0
      ? ((changeAmount / previousMonthIncome) * 100).toFixed(1)
      : '0.0';
  const isPositive = changeAmount >= 0;

  return (
    <Card
      data-section="income"
      data-testid="home-card-kpi"
      className="h-full min-h-0 flex flex-col transition-all duration-200 hover:shadow-md hover:border-primary/20 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
        <CardTitle className="text-sm font-medium">Income</CardTitle>
        <div className="p-2 bg-green-500/10 rounded-full">
          <DollarSign className="h-4 w-4 text-green-500" />
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-hidden">
        <div className="text-lg sm:text-2xl font-bold truncate">
          {fmt(currentMonthIncome)}
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          {isPositive ? (
            <TrendingUp className="h-3 w-3 text-green-500" />
          ) : (
            <TrendingDown className="h-3 w-3 text-red-500" />
          )}
          <span className={isPositive ? "text-green-500" : "text-red-500"}>
            {isPositive ? "+" : ""}
            {changePercentage}%
          </span>
          <span>from last month</span>
        </p>
      </CardContent>
    </Card>
  );
}
